import type { Store, RuntimePiece, HistoryEntry } from '../game/state';
import { isSnappable, normalizeAngle, defaultSnapThreshold } from '../game/snap';
import type { HitTester } from '../render/hitTest';

export interface Point {
  x: number;
  y: number;
}

export interface InputCallbacks {
  getMetrics(): {
    pieceW: number;
    pieceH: number;
    trayScale: number;
    boardX: number;
    boardY: number;
    boardW: number;
    boardH: number;
  };
  /** 指针事件 → canvas CSS 坐标（游戏世界）。 */
  getCanvasPoint(e: { clientX: number; clientY: number }): Point;
  onFirstInteraction(): void;
  onPlaced(piece: RuntimePiece, fromX: number, fromY: number): void;
  onPick(): void;
  onComplete(): void;
}

/**
 * 统一指针交互（鼠标 + 触摸）：
 *  - 单指/左键：拾起、拖拽、吸附
 *  - 双指(pinch)：捏合缩放画板（移动端无滚轮，靠双指）
 *  - 旋转：右键 / 双击 / R 键
 */
export class InputController {
  private lastPickedId: number | null = null;
  private activePointerId: number | null = null;
  private pending: HistoryEntry | null = null;

  /** 活跃指针（屏幕坐标，用于 pinch 距离计算）。 */
  private readonly pointers = new Map<number, { x: number; y: number }>();
  private pinching = false;
  private pinchStartDist = 0;
  private pinchStartZoom = 1;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly store: Store,
    private readonly hitTester: HitTester,
    private readonly cb: InputCallbacks,
  ) {
    canvas.addEventListener('pointerdown', this.onDown);
    canvas.addEventListener('pointermove', this.onMove);
    canvas.addEventListener('pointerup', this.onUp);
    canvas.addEventListener('pointercancel', this.onUp);
    canvas.addEventListener('contextmenu', this.onContextMenu);
    canvas.addEventListener('dblclick', this.onDblClick);
    window.addEventListener('keydown', this.onKey);
  }

  dispose(): void {
    this.canvas.removeEventListener('pointerdown', this.onDown);
    this.canvas.removeEventListener('pointermove', this.onMove);
    this.canvas.removeEventListener('pointerup', this.onUp);
    this.canvas.removeEventListener('pointercancel', this.onUp);
    this.canvas.removeEventListener('contextmenu', this.onContextMenu);
    this.canvas.removeEventListener('dblclick', this.onDblClick);
    window.removeEventListener('keydown', this.onKey);
  }

  private cancelDrag(): void {
    this.store.mutate((s) => {
      s.draggingId = null;
    });
    this.activePointerId = null;
    this.pending = null;
  }

  private onDown = (e: PointerEvent) => {
    if (e.button !== 0) return;
    if (this.store.get().status === 'completed') return;

    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // 第二指落下 → 进入 pinch（取消正在进行的拖拽）
    if (this.pointers.size === 2 && !this.pinching) {
      e.preventDefault();
      this.cancelDrag();
      this.pinching = true;
      const [a, b] = [...this.pointers.values()];
      this.pinchStartDist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      this.pinchStartZoom = this.store.get().zoom;
      return;
    }

    if (this.pointers.size !== 1 || this.pinching) return;

    // 单指：尝试拾起拖拽
    const { x, y } = this.cb.getCanvasPoint(e);
    const piece = this.hitTester.pick(this.store.get().pieces, x, y);
    if (!piece) return;

    e.preventDefault();
    this.activePointerId = e.pointerId;
    try {
      this.canvas.setPointerCapture(e.pointerId);
    } catch {
      /* 部分环境不支持，忽略 */
    }
    this.lastPickedId = piece.id;
    this.pending = {
      id: piece.id,
      x: piece.currentX,
      y: piece.currentY,
      rot: piece.rotation,
      placed: piece.placed,
    };
    this.cb.onPick();

    const m = this.cb.getMetrics();
    const inBoard =
      x >= m.boardX && x <= m.boardX + m.boardW && y >= m.boardY && y <= m.boardY + m.boardH;
    const vs = inBoard ? m.pieceW : m.pieceW * m.trayScale;
    const hs = inBoard ? m.pieceH : m.pieceH * m.trayScale;

    if (this.store.get().status === 'ready') this.cb.onFirstInteraction();

    this.store.mutate((st) => {
      st.draggingId = piece.id;
      st.dragOffsetX = vs > 0 ? (x - piece.currentX) / vs : 0;
      st.dragOffsetY = hs > 0 ? (y - piece.currentY) / hs : 0;
      piece.zIndex = (st.topZ += 1);
    });
    this.store.emit();
  };

  private onMove = (e: PointerEvent) => {
    if (!this.pointers.has(e.pointerId)) return;
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // 双指 pinch：按屏幕距离比缩放画板
    if (this.pinching && this.pointers.size >= 2) {
      const [a, b] = [...this.pointers.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const zoom = Math.min(3, Math.max(0.4, (this.pinchStartZoom * dist) / this.pinchStartDist));
      this.store.mutate((s) => {
        s.zoom = zoom;
      });
      return;
    }

    // 单指拖拽
    const st = this.store.get();
    if (st.draggingId === null) return;
    if (this.activePointerId !== e.pointerId) return;

    const { x, y } = this.cb.getCanvasPoint(e);
    const m = this.cb.getMetrics();
    const inBoard =
      x >= m.boardX && x <= m.boardX + m.boardW && y >= m.boardY && y <= m.boardY + m.boardH;
    const vs = inBoard ? m.pieceW : m.pieceW * m.trayScale;
    const hs = inBoard ? m.pieceH : m.pieceH * m.trayScale;
    const draggingId = st.draggingId;
    this.store.mutate((s) => {
      const p = s.pieces[draggingId];
      if (p && !p.placed) {
        p.currentX = x - s.dragOffsetX * vs;
        p.currentY = y - s.dragOffsetY * hs;
      }
    });
  };

  private onUp = (e: PointerEvent) => {
    this.pointers.delete(e.pointerId);

    // pinch 中：任一指抬起 → 结束 pinch
    if (this.pinching) {
      if (this.pointers.size < 2) {
        this.pinching = false;
        this.cancelDrag();
      }
      return;
    }

    // 单指拖拽释放
    const st = this.store.get();
    if (st.draggingId === null) return;
    if (this.activePointerId !== e.pointerId) return;

    const id = st.draggingId;
    this.store.mutate((s) => {
      s.draggingId = null;
    });
    this.activePointerId = null;
    this.trySnap(id);
    if (this.pending) {
      const snap = this.pending;
      this.store.mutate((s) => {
        s.history.push(snap);
      });
      this.pending = null;
    }
    this.store.emit();
  };

  private trySnap(id: number): void {
    const st = this.store.get();
    const p = st.pieces[id];
    if (!p || p.placed) return;

    const m = this.cb.getMetrics();
    const correctX = st.boardX + p.baseX;
    const correctY = st.boardY + p.baseY;
    const threshold = defaultSnapThreshold(m.pieceW, m.pieceH);

    const ok = isSnappable({
      currentCenterX: p.currentX + m.pieceW / 2,
      currentCenterY: p.currentY + m.pieceH / 2,
      correctCenterX: correctX + m.pieceW / 2,
      correctCenterY: correctY + m.pieceH / 2,
      rotation: p.rotation,
      threshold,
    });
    if (!ok) return;

    const fromX = p.currentX;
    const fromY = p.currentY;
    this.store.mutate((s) => {
      const pp = s.pieces[id];
      pp.currentX = correctX;
      pp.currentY = correctY;
      pp.rotation = 0;
      pp.placed = true;
      s.moves += 1;
      s.placedCount += 1;
    });
    this.cb.onPlaced(p, fromX, fromY);

    if (this.store.get().pieces.every((pp) => pp.placed)) {
      this.cb.onComplete();
    }
  }

  private rotatePiece(id: number): void {
    this.store.mutate((st) => {
      const p = st.pieces[id];
      if (p && !p.placed) {
        p.rotation = normalizeAngle(p.rotation + 90);
        p.zIndex = (st.topZ += 1);
      }
    });
    this.store.emit();
  }

  private onContextMenu = (e: MouseEvent) => {
    e.preventDefault();
    const { x, y } = this.cb.getCanvasPoint(e);
    const piece = this.hitTester.pick(this.store.get().pieces, x, y);
    const id = piece?.id ?? this.lastPickedId;
    if (id !== null) this.rotatePiece(id);
  };

  private onDblClick = (e: MouseEvent) => {
    const { x, y } = this.cb.getCanvasPoint(e);
    const piece = this.hitTester.pick(this.store.get().pieces, x, y);
    if (piece) this.rotatePiece(piece.id);
  };

  private onKey = (e: KeyboardEvent) => {
    if (e.key === 'r' || e.key === 'R') {
      if (this.lastPickedId !== null) this.rotatePiece(this.lastPickedId);
    }
  };
}
